import React, { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { useSelector } from "react-redux"
import { FiLock, FiCheck, FiX } from "react-icons/fi"

import IconBtn from "../../common/IconBtn"
import {
  getQuiz,
  submitQuiz,
  getQuizAttempts,
} from "../../../services/operations/quizAPI"

//The end of course quiz.
//
//The questions arrive without their answers and grading happens on the server,
//so nothing here can be inspected or edited to reveal the result early. The lock
//below is only what the server already told us...it re-checks on submit anyway.
const CourseQuiz = () => {
  const { courseId } = useParams()
  const { token } = useSelector((state) => state.auth)

  const [loading, setLoading] = useState(true)
  const [state, setState] = useState({ quiz: null, locked: false, missing: false })
  const [progress, setProgress] = useState(null)
  const [history, setHistory] = useState(null)

  const [answers, setAnswers] = useState({})
  const [result, setResult] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  const load = async () => {
    setLoading(true)
    const outcome = await getQuiz(courseId, token)
    setState(outcome)
    setProgress(outcome.progress || null)

    if (!outcome.locked && !outcome.missing) {
      setHistory(await getQuizAttempts(courseId, token))
    }

    setLoading(false)
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId])

  const handleSubmit = async () => {
    const ordered = state.quiz.questions.map((_, index) =>
      answers[index] === undefined ? -1 : answers[index]
    )

    setSubmitting(true)
    const outcome = await submitQuiz(courseId, ordered, token)
    setSubmitting(false)

    if (outcome) {
      setResult(outcome)
      setHistory(await getQuizAttempts(courseId, token))
      window.scrollTo({ top: 0, behavior: "smooth" })
    }
  }

  const retake = () => {
    setResult(null)
    setAnswers({})
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  if (loading) {
    return (
      <div className="grid min-h-[calc(100vh-3.5rem)] place-items-center">
        <div className="spinner"></div>
      </div>
    )
  }

  if (state.missing) {
    return (
      <div className="py-16 text-center">
        <h1 className="text-2xl font-semibold text-richblack-5">No quiz yet</h1>
        <p className="mt-2 text-richblack-300">
          Your instructor has not added a quiz to this course.
        </p>
      </div>
    )
  }

  if (state.locked) {
    return (
      <div className="mx-auto mt-16 max-w-[560px] rounded-lg border border-richblack-700 bg-richblack-800 p-10 text-center">
        <FiLock className="mx-auto text-4xl text-yellow-50" />
        <h1 className="mt-4 text-2xl font-semibold text-richblack-5">
          Quiz locked
        </h1>
        <p className="mt-2 text-richblack-300">
          Finish every lecture in this course to unlock the quiz.
        </p>

        {progress && (
          <div className="mt-6">
            <div className="h-2 w-full overflow-hidden rounded-full bg-richblack-700">
              <div
                className="h-full rounded-full bg-yellow-50 transition-all"
                style={{ width: `${progress.percentage}%` }}
              />
            </div>
            <p className="mt-2 text-sm text-richblack-300">
              {progress.completedLectures} of {progress.totalLectures} lectures
              complete ({progress.percentage}%)
            </p>
          </div>
        )}
      </div>
    )
  }

  //graded view
  if (result) {
    return (
      <div className="py-10">
        <div
          className={`rounded-lg border p-8 text-center ${
            result.passed
              ? "border-caribbeangreen-300 bg-richblack-800"
              : "border-pink-200 bg-richblack-800"
          }`}
        >
          <p className="text-5xl font-bold text-richblack-5">{result.score}%</p>
          <p
            className={`mt-2 text-xl font-semibold ${
              result.passed ? "text-caribbeangreen-300" : "text-pink-200"
            }`}
          >
            {result.passed ? "Passed" : "Not passed"}
          </p>
          <p className="mt-2 text-richblack-300">
            {result.correctCount} of {result.totalQuestions} correct. Pass mark is{" "}
            {result.passingScore}%.
          </p>
          <p className="mt-1 text-sm text-richblack-400">
            Best score so far: {result.bestScore}%
          </p>

          <div className="mt-6">
            <IconBtn onClick={retake} text="Try again" />
          </div>
        </div>

        <h2 className="mt-10 text-xl font-semibold text-richblack-5">Review</h2>

        <div className="mt-4 flex flex-col gap-4">
          {result.review.map((item, index) => (
            <div
              key={item._id}
              className="rounded-md border border-richblack-700 bg-richblack-800 p-5"
            >
              <div className="flex items-start gap-3">
                {item.isCorrect ? (
                  <FiCheck className="mt-1 shrink-0 text-caribbeangreen-300" />
                ) : (
                  <FiX className="mt-1 shrink-0 text-pink-200" />
                )}
                <p className="font-medium text-richblack-5">
                  {index + 1}. {item.questionText}
                </p>
              </div>

              <div className="mt-3 flex flex-col gap-2 pl-7">
                {item.options.map((option, oIndex) => {
                  const isAnswer = oIndex === item.correctOption
                  const isChosen = oIndex === item.chosenOption

                  return (
                    <div
                      key={oIndex}
                      className={`rounded px-3 py-2 text-sm ${
                        isAnswer
                          ? "bg-caribbeangreen-700 text-caribbeangreen-25"
                          : isChosen
                          ? "bg-pink-700 text-pink-25"
                          : "bg-richblack-700 text-richblack-100"
                      }`}
                    >
                      {option}
                      {isAnswer && " — correct answer"}
                      {isChosen && !isAnswer && " — your answer"}
                    </div>
                  )
                })}
              </div>

              {item.explanation && (
                <p className="mt-3 pl-7 text-sm text-richblack-300">
                  {item.explanation}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    )
  }

  //taking the quiz
  const { quiz } = state
  const answered = Object.keys(answers).length

  return (
    <div className="py-10">
      <h1 className="text-3xl font-semibold text-richblack-5">{quiz.title}</h1>
      {quiz.description && (
        <p className="mt-2 text-richblack-300">{quiz.description}</p>
      )}

      <p className="mt-2 text-sm text-richblack-400">
        {quiz.totalQuestions} questions · pass mark {quiz.passingScore}% · unlimited
        retakes
        {history?.totalAttempts > 0 &&
          ` · best so far ${history.bestScore}% over ${history.totalAttempts} attempt(s)`}
      </p>

      <div className="mt-8 flex flex-col gap-5">
        {quiz.questions.map((question, index) => (
          <div
            key={question._id}
            className="rounded-md border border-richblack-700 bg-richblack-800 p-5"
          >
            <p className="font-medium text-richblack-5">
              {index + 1}. {question.questionText}
            </p>

            <div className="mt-3 flex flex-col gap-2">
              {question.options.map((option, oIndex) => (
                <label
                  key={oIndex}
                  className={`flex cursor-pointer items-center gap-3 rounded px-3 py-2 ${
                    answers[index] === oIndex
                      ? "bg-richblack-700 text-richblack-5"
                      : "text-richblack-100"
                  }`}
                >
                  <input
                    type="radio"
                    name={`question-${index}`}
                    checked={answers[index] === oIndex}
                    onChange={() =>
                      setAnswers((prev) => ({ ...prev, [index]: oIndex }))
                    }
                    className="h-4 w-4 accent-yellow-50"
                  />
                  {option}
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 flex items-center gap-4">
        <IconBtn
          onClick={handleSubmit}
          disabled={submitting}
          text={submitting ? "Submitting..." : "Submit quiz"}
        />
        <span className="text-sm text-richblack-300">
          {answered} of {quiz.totalQuestions} answered
        </span>
      </div>
    </div>
  )
}

export default CourseQuiz
